import { chain } from 'lodash'
import { observer } from 'mobx-react-lite'
import { type BooleanMap, completedTestRunStatuses, type TestFile, type TestRun, TestRunStatus, type TestRunTest, TestStatus } from 'models/test-run'
import { useState } from 'react'
import { Button, Checkbox, Icon, Input, Pagination } from 'semantic-ui-react'
import { AppStore, ProjectStore, TestRunStore } from 'stores'
import TestMultiStatusSelect from '../../components/form/test-multi-status-select'
import Spinner from '../../components/spinner'
import TestResultRow from '../../components/test-result-row'
import Project from '../../models/project'

interface Props {
  testRun: TestRun
  refresh: (testRunId: number) => any
  testFiles: TestFile[]
  running: boolean
  rootCauseId: number
}

const TestResultsTab = observer(({ testRun, refresh, testFiles, running, rootCauseId }: Props) => {
  const [page, setPage] = useState(1)
  const [search, setSearch] = useState('')
  const [selectedForRerun, setSelectedForRerun] = useState<TestRunTest[]>([])
  const [showQuarantinedTests, setShowQuarantinedTests] = useState<boolean>(false)

  function toggleRerunOption (testRunTest: TestRunTest, rerun: boolean): void {
    if (rerun && !selectedForRerun.includes(testRunTest)) {
      setSelectedForRerun([...selectedForRerun, testRunTest])
    } else if (!rerun) {
      setSelectedForRerun(selectedForRerun.filter(trt => trt.id !== testRunTest.id))
    }
  }

  const results = chain(testFiles).sortBy(['latestStartTime', 'name']).groupBy('status').value()

  const hasFailedTests = results[TestStatus.FAIL] !== undefined && results[TestStatus.FAIL].length > 0
  const allowRerun = hasFailedTests && !running
  const loadingResults = TestRunStore.loadingTestRunResults

  const testResults = [
    ...(results[TestStatus.RUNNING] ?? []),
    ...(results[TestStatus.NOT_RUN] ?? []),
    ...(results[TestStatus.FAIL] ?? []),
    ...(results[TestStatus.IGNORED] ?? []),
    ...(results[TestStatus.PASS] ?? []),
    ...(results[TestStatus.PENDING] ?? [])
  ]

  const filteredStatuses = TestRunStore.getStatusFilters(testRun.id)

  const matchesFilter = (testRunTest: TestRunTest): boolean =>
    testRunTest.selected ||
    ((filteredStatuses.length === 0 ||
        filteredStatuses.some((s) => s === testRunTest.status) ||
        (filteredStatuses.some((s) => s === TestStatus.FLAKY) && testRunTest.flaky)) &&
      (!testRunTest.quarantined || showQuarantinedTests === testRunTest.quarantined) &&
      (search === '' || testRunTest.projectTest.matches(search.toLocaleLowerCase())) &&
      (rootCauseId === 0 || testRunTest.testRunAnalysisRootCauseId === rootCauseId))

  let filteredFiles = testResults.filter((file) => file.latestTests.some((t) => matchesFilter(t)))

  let filteredCount = 0
  testFiles.forEach((f) => {
    f.latestTests.forEach((t) => {
      if (matchesFilter(t)) {
        filteredCount++
      }
    })
  })

  const openItems = TestRunStore.getOpenItems(testRun.id)
  const aboveResultCutoff = filteredCount > 200
  const openDefault = !aboveResultCutoff

  let pagination: JSX.Element | null = null
  const pageSize = 20

  if (testRun === null || testRun === undefined) return null

  const project = ProjectStore.find(testRun.projectId) ?? new Project()

  const rerunTests = async (testResults: TestRunTest[]): Promise<void> => {
    const currentlyRunning =
      testRun.status === TestRunStatus.SUBMITTED || testRun.status === TestRunStatus.SETUP || testRun.status === TestRunStatus.RUNNING || testRun.status === TestRunStatus.TEAR_DOWN
    await TestRunStore.rerunTests(testResults)
    if (!currentlyRunning) {
      refresh(testRun.id)
    }
  }

  const rerunFailedTests = async (): Promise<void> => {
    const currentlyRunning =
      testRun.status === TestRunStatus.SUBMITTED || testRun.status === TestRunStatus.SETUP || testRun.status === TestRunStatus.RUNNING || testRun.status === TestRunStatus.TEAR_DOWN
    await TestRunStore.rerunFailedTests(testRun.id)
    if (!currentlyRunning) {
      refresh(testRun.id)
    }
  }

  const rerunFailedTestsSingleRunner = async (): Promise<void> => {
    const currentlyRunning =
      testRun.status === TestRunStatus.SUBMITTED || testRun.status === TestRunStatus.SETUP || testRun.status === TestRunStatus.RUNNING || testRun.status === TestRunStatus.TEAR_DOWN
    await TestRunStore.rerunFailedTestsSingleRunner(testRun.id)
    if (!currentlyRunning) {
      refresh(testRun.id)
    }
  }

  const rerunSelectedTests = async (): Promise<void> => {
    const currentlyRunning =
      testRun.status === TestRunStatus.SUBMITTED || testRun.status === TestRunStatus.SETUP || testRun.status === TestRunStatus.RUNNING || testRun.status === TestRunStatus.TEAR_DOWN
    await TestRunStore.rerunTests(selectedForRerun)
    if (!currentlyRunning) {
      refresh(testRun.id)
    }
  }

  if (aboveResultCutoff && filteredFiles.length > pageSize) {
    pagination = (
      <Pagination
        totalPages={Math.ceil(filteredFiles.length / pageSize)}
        lastItem={false}
        firstItem={false}
        defaultActivePage={1}
        onPageChange={(_, a) => {
          setPage(a.activePage as number)
        }}
      />
    )
    filteredFiles = filteredFiles.filter(
      (_, i) => i >= pageSize * (page - 1) && i < pageSize * page
    )
  }

  const isOpen = (i: number): boolean => openItems[i] ?? openDefault

  const resultRows = filteredFiles.map((file) => (
    <TestResultRow
      filter={matchesFilter}
      testRun={testRun}
      key={file.id}
      open={isOpen(file.id)}
      toggleOpen={() => {
        TestRunStore.updateOpenItems(testRun.id, {
          ...openItems,
          [file.id]: !isOpen(file.id)
        })
      }
      }
      testFile={file}
      rerunTests={rerunTests}
      project={project}
      testTimeoutSeconds={testRun.testTimeoutSeconds}
      toggleRerunOption={toggleRerunOption}
      refresh={refresh}
    />
  ))

  const toggleCollapse = (open: boolean): void => {
    const data: BooleanMap = {}
    filteredFiles.forEach((f) => {
      data[f.id] = open
    })
    TestRunStore.updateOpenItems(testRun.id, data)
  }

  return (
    <div className="test-results-tab">
      {!loadingResults && (
        <header>
          <div className="title">
            <h2>Test Results</h2>
            <div className="count">
              {filteredCount}
              {filteredStatuses.length === 0 ? '' : ' Filtered'} Test Cases
            </div>
            <Button data-testid="collapse-all-button" inverted color="grey" size="mini" onClick={() => { toggleCollapse(false) }}>
              <Icon name="minus" /> Collapse All
            </Button>
            <Button data-testid="expand-all-button" inverted color="grey" size="mini" onClick={() => { toggleCollapse(true) }}>
              <Icon name="plus" /> Expand All
            </Button>
            <Input
              icon={{
                name: search === '' ? 'search' : 'close',
                link: true,
                onClick: () => { setSearch('') }
              }}
              placeholder="Search..."
              value={search}
              onChange={(e) => { setSearch(e.target.value) }}
            />
            <TestMultiStatusSelect
              placeholder="Any Status"
              value={filteredStatuses}
              onChange={(filters) => TestRunStore.updateStatusFilters(testRun.id, filters)}
            />
            <Checkbox checked={showQuarantinedTests} label={'Show Quarantined Tests'}
                      onChange={() => { setShowQuarantinedTests(!showQuarantinedTests) }} />
            {AppStore.account.isLegacyOrTierTwoPlusPlan() && (
              <div className="rerun-actions">
                {allowRerun && (
                  <Button data-testid="rerun-failed-tests-button" size="small" color="green" onClick={() => { void rerunFailedTests() }}>
                    <Icon name="redo" />
                    Rerun Failed Tests
                  </Button>
                )}
                {allowRerun && (
                  <Button data-testid="rerun-failed-tests-serially-button" size="small" color="green" onClick={() => { void rerunFailedTestsSingleRunner() }}>
                    <Icon name="redo" />
                    Rerun Failed Tests (Serially)
                  </Button>
                )}
                <Button data-testid="rerun-selected-tests-button" size="small" color="green" onClick={() => { void rerunSelectedTests() }} disabled={selectedForRerun.length === 0 || !completedTestRunStatuses.includes(testRun.status)}>
                  <Icon name="redo" />
                  Rerun Selected Tests
                </Button>
              </div>
            )}
            <br />
            {pagination}
          </div>
        </header>
      )}
      {!loadingResults && (
        <table id="test-run-result-table" className="report-test-results-list table">
          <tbody>{resultRows}</tbody>
        </table>
      )}
      {loadingResults && <Spinner text="Loading Test Results" />}
    </div>
  )
})

export default TestResultsTab
